import { useEffect, useState } from "react";
import logger from "../../util/logger";
import { FaChevronLeft } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

import { auth, db } from "../../util/firebase";
import { query, collection, getDocs, where, addDoc } from "firebase/firestore";

import BarLoader from "react-spinners/BarLoader";
import Avatar from "../../components/Avatar/Avatar";
import { useAuthState } from "react-firebase-hooks/auth";

import { v4 as uuidv4 } from "uuid";
import Swal from "sweetalert2";
import emailjs from "@emailjs/browser";

function SingleUserPage() {
  // eslint-disable-next-line
  const [isLoading, setIsLoading] = useState(false);

  const [user] = useAuthState(auth);

  const navigate = useNavigate();

  const params = useParams();
  const friendUsername = params.friendUsername;

  const [myUserData, setMyUserData] = useState();
  const fetchMyUserData = async (isMounted) => {
    try {
      setIsLoading(true);
      const q = query(collection(db, "users"), where("uid", "==", user?.uid));
      const doc = await getDocs(q);
      const data = doc.docs[0].data();
      if (isMounted.current) {
        setMyUserData(data);
        setIsLoading(false);
      }
    } catch (err) {
      console.error(err);
      if (isMounted.current) {
        setIsLoading(false);
        Swal.fire({
          text: "An error occurred while fetching user data",
          icon: "error",
          confirmButtonColor: "#005EE1",
          confirmButtonText: "OKAY",
        });
      }
    }
  };

  useEffect(() => {
    const isMounted = { current: true };
    if (user) {
      fetchMyUserData(isMounted);
    }
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line
  }, [user]);

  const [userData, setUserData] = useState();
  const [userChampionTier, setUserChampionTier] = useState();
  const fetchUserData = async (isMounted) => {
    try {
      setIsLoading(true);
      const q = query(
        collection(db, "users"),
        where("username", "==", friendUsername),
      );
      const doc = await getDocs(q);
      const data = doc.docs[0].data();

      const championPoints = data.championPoints;
      let tier;
      if (championPoints === 0) {
        tier = "TIER 0";
      } else if (championPoints >= 1 && championPoints <= 99) {
        tier = "TIER 1";
      } else if (championPoints >= 100 && championPoints <= 199) {
        tier = "TIER 2";
      } else if (championPoints >= 200 && championPoints <= 299) {
        tier = "TIER 3";
      } else if (championPoints >= 300 && championPoints <= 399) {
        tier = "TIER 4";
      } else if (championPoints >= 400 && championPoints <= 499) {
        tier = "TIER 5";
      } else if (championPoints >= 500 && championPoints <= 599) {
        tier = "TIER 6";
      } else if (championPoints >= 600 && championPoints <= 699) {
        tier = "TIER 7";
      } else if (championPoints >= 700 && championPoints <= 799) {
        tier = "TIER 8";
      } else if (championPoints >= 800 && championPoints <= 899) {
        tier = "TIER 9";
      } else if (championPoints >= 900) {
        tier = "TIER 10";
      }

      if (isMounted.current) {
        setUserChampionTier(tier);
        setUserData(data);
        setIsLoading(false);
      }
    } catch (err) {
      console.error(err);
      if (isMounted.current) {
        setIsLoading(false);
        Swal.fire({
          text: "An error occurred while fetching user data",
          icon: "error",
          confirmButtonColor: "#005EE1",
          confirmButtonText: "OKAY",
        });
      }
    }
  };

  useEffect(() => {
    const isMounted = { current: true };
    if (friendUsername) {
      fetchUserData(isMounted);
    }
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line
  }, [friendUsername]);

  const [achievementsUnlocked, setAchievementsUnlocked] = useState();
  const fetchAchievements = async (isMounted) => {
    try {
      setIsLoading(true);

      let fetchedAchievementsUnlocked = 0;

      const q = query(collection(db, "achievements"));
      const docs = await getDocs(q);
      docs.forEach((doc) => {
        const newAchievement = doc.data();
        if (newAchievement.achieverUids.includes(userData.uid)) {
          fetchedAchievementsUnlocked += 1;
        }
      });

      if (isMounted.current) {
        setAchievementsUnlocked(fetchedAchievementsUnlocked);
        setIsLoading(false);
      }
    } catch (err) {
      console.error(err);
      if (isMounted.current) {
        setIsLoading(false);
        Swal.fire({
          text: "An error occurred while fetching achievements unlocked",
          icon: "error",
          confirmButtonColor: "#005EE1",
          confirmButtonText: "OKAY",
        });
      }
    }
  };

  useEffect(() => {
    const isMounted = { current: true };
    if (userData) {
      fetchAchievements(isMounted);
    }
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line
  }, [userData]);

  const [userStatus, setUserStatus] = useState();
  const fetchUserRelationships = async (isMounted) => {
    try {
      setIsLoading(true);

      const q = query(
        collection(db, "userRelationships"),
        where("users", "array-contains", user?.uid),
      );
      const doc = await getDocs(q);
      const data = doc.docs.map((doc) => doc.data());

      let userFriends = data.filter(
        (relationship) => relationship.status === "accepted",
      );
      let userFriendRequests = data.filter(
        (relationship) => relationship.status === "requested",
      );
      logger.debug(userFriendRequests);

      if (isMounted.current) {
        // eslint-disable-next-line
        userFriends = userFriends.map((friend) => {
          logger.debug(friend);
          if (
            friend.firstUserUsername === friendUsername ||
            friend.secondUserUsername === friendUsername
          ) {
            setUserStatus("Friend");
          }
        });

        // eslint-disable-next-line
        userFriendRequests = userFriendRequests.map((request) => {
          if (
            request.firstUserUsername === friendUsername ||
            request.secondUserUsername === friendUsername
          ) {
            setUserStatus("Friend Request Sent");
          }
        });

        setIsLoading(false);
      }
    } catch (err) {
      console.error(err);
      if (isMounted.current) {
        setIsLoading(false);
        Swal.fire({
          text: "An error occurred while fetching user data",
          icon: "error",
          confirmButtonColor: "#005EE1",
          confirmButtonText: "OKAY",
        });
      }
    }
  };

  useEffect(() => {
    const isMounted = { current: true };
    if (user) {
      fetchUserRelationships(isMounted);
    }
    return () => {
      isMounted.current = false;
    };
    // eslint-disable-next-line
  }, [user]);

  const sendFriendRequest = async () => {
    try {
      if (!myUserData || !userData) {
        Swal.fire({
          text: "User data not loaded yet. Please wait.",
          icon: "warning",
          confirmButtonColor: "#005EE1",
          confirmButtonText: "OKAY",
        });
        return;
      }

      setIsLoading(true);

      const newUserRelationship = {
        uid: uuidv4(),
        users: [myUserData.uid, userData.uid],
        firstUserUid: myUserData.uid,
        firstUserUsername: myUserData.username,
        firstUserAvatar: myUserData.avatar || "",
        secondUserUid: userData.uid,
        secondUserUsername: userData.username,
        secondUserAvatar: userData.avatar || "",
        status: "requested",
      };

      await addDoc(collection(db, "userRelationships"), newUserRelationship);

      Swal.fire({
        text: "Friend request sent successfully!",
        icon: "success",
        confirmButtonColor: "#005EE1",
        confirmButtonText: "OKAY",
      });

      // Send in-app notification
      const newNotification = {
        uid: uuidv4(),
        title: "Friend Request",
        description: `${myUserData.username} has sent you a friend request.`,
        recipientUid: userData.uid,
        dateCreated: new Date().toISOString(),
        isRead: false,
      };
      await addDoc(collection(db, "notifications"), newNotification);
      //

      // Send email notification
      const serviceId = "sportleague_outlook";
      const templateId = "friendRequest";
      const publicKey = "Ki5BETKiWrS6RDEP_";

      const templateParams = {
        toEmail: userData.email,
        toUsername: userData.username,
        fromUsername: myUserData.username,
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey).then(
        (response) => {
          logger.info("SUCCESS!", response.status, response.text);
        },
        (error) => {
          logger.error("FAILED...", error);
        },
      );
      //

      const isMounted = { current: true };
      await fetchUserRelationships(isMounted);

      setIsLoading(false);
    } catch (err) {
      console.error(err);
      setIsLoading(false);
      Swal.fire({
        text: "An error occurred while sending friend request",
        icon: "error",
        confirmButtonColor: "#005EE1",
        confirmButtonText: "OKAY",
      });
    }
  };

  if (isLoading)
    return (
      <div className="loadingPage">
        <img
          alt="logo"
          src="https://firebasestorage.googleapis.com/v0/b/sport-league-e35d7.appspot.com/o/assets%2Flogos%2F01%20-%20SL%20Logo%203.png?alt=media&token=2e5a6320-d2d7-45f8-9bc9-a8f9c7f8caa6"
          style={{
            width: 100,
            marginBottom: "1rem",
          }}
        />
        <BarLoader color="#005EE1" />
      </div>
    );

  return (
    <div id="singleUserPage" className="page">
      <h1>
        <FaChevronLeft
          onClick={() => navigate(-1)}
          style={{
            color: "#005EE1",
            width: 35,
            height: 35,
            marginTop: -8,
            marginRight: "0.25rem",
            cursor: "pointer",
          }}
        />
      </h1>
      <div style={{ marginTop: "1rem" }}>
        <img
          onClick={() => navigate("/account/profile")}
          alt="bannerImage"
          src={userData?.bannerDesktop}
          style={{ width: "100%", borderRadius: 20, cursor: "pointer" }}
          className="hideOnMobile"
        />
        <img
          onClick={() => navigate("/account/profile")}
          alt="bannerImage"
          src={userData?.bannerMobile}
          style={{ width: "100%", borderRadius: 20, cursor: "pointer" }}
          className="hideOnDesktop"
        />
        <div
          onClick={() => navigate("/account/profile")}
          className="underProfileBannerContainer"
        >
          <Avatar
            alt={userData?.username || "avatar"}
            src={userData?.avatar}
            size={168}
            user={userData}
          />
          <div className="underProfileBannerTextContainer">
            <p
              style={{
                color: "#FFFFFF",
                marginBottom: 0,
                fontSize: 32,
                fontWeight: 700,
              }}
            >
              {userData?.username}
            </p>
            <div
              style={{ display: "flex", flexDirection: "row", fontSize: 15 }}
            >
              <p style={{ color: "#FFFFFF", marginRight: "1rem" }}>
                ⭐ {userData?.starCoins ? userData.starCoins : 0}
              </p>
              <p style={{ color: "#FFFFFF", marginRight: "1rem" }}>
                🏆 {userData?.championPoints ? userData.championPoints : 0}
              </p>
              <p style={{ color: "#FFFFFF" }}>
                🎟️ {userData?.tickets ? userData.tickets : 0}
              </p>
            </div>
          </div>
        </div>
        <div
          onClick={() => navigate(`/champions-road/${userData.uid}`)}
          className="accountPageMenuItem"
        >
          <p>
            CHAMPIONS ROAD <span>{userChampionTier}</span>
          </p>
        </div>
        <div
          onClick={() => navigate(`/achievements/${userData.uid}`)}
          className="accountPageMenuItem"
        >
          <p>
            ACHIEVEMENTS <span>{achievementsUnlocked}</span>
          </p>
        </div>
        {userStatus === "Friend" ? null : userStatus ===
          "Friend Request Sent" ? (
          <button className="primaryBtn" disabled>
            FRIEND REQUEST SENT
          </button>
        ) : (
          <button className="primaryBtn" onClick={sendFriendRequest}>
            SEND FRIEND REQUEST
          </button>
        )}
      </div>
    </div>
  );
}

export default SingleUserPage;
